import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  Select,
  Box,
  useToast,
  IconButton,
  Divider,
  Spinner,
  Center
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import api from '../../../../services/api';
import { useSocket } from '../../../../contexts/SocketContext';
import { useNewKBLeadForm } from '../context/NewKBLeadFormContext';

/**
 * NewKBCommentsForm - Enhanced comments management for NewKBLeadModal
 * Includes real-time updates via socket events
 */
const NewKBCommentsForm = () => {
  const { formData, isEditMode } = useNewKBLeadForm();
  const { socket } = useSocket();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const leadId = formData?.leadId || formData?._id;

  const fetchComments = async () => {
    if (!leadId) return;
    
    setIsLoading(true);
    try {
      const response = await api.get(`/api/comments/lead/${leadId}`);
      setComments(response.data.comments || []);
      console.log('[NewKBCommentsForm] Fetched comments:', response.data.comments);
    } catch (error) {
      console.error('[NewKBCommentsForm] Error fetching comments:', error);
      toast({
        title: 'Error fetching comments',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch when component mounts or leadId changes
  useEffect(() => {
    if (leadId) {
      fetchComments();
    }
  }, [leadId]);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !leadId) return;

    // Handler for comment added event
    const handleCommentAdded = (data) => {
      if (data.leadId === leadId) {
        console.log('[SOCKET] Received comment:added event', data);
        fetchComments(); // Refresh comments
      }
    };

    // Handler for comment updated event
    const handleCommentUpdated = (data) => {
      if (data.leadId === leadId) {
        console.log('[SOCKET] Received comment:updated event', data);
        fetchComments(); // Refresh comments
      }
    };

    // Handler for comment deleted event
    const handleCommentDeleted = (data) => {
      if (data.leadId === leadId) {
        console.log('[SOCKET] Received comment:deleted event', data);
        fetchComments(); // Refresh comments
      }
    };

    // Register socket event listeners
    socket.on('comment:added', handleCommentAdded);
    socket.on('comment:updated', handleCommentUpdated);
    socket.on('comment:deleted', handleCommentDeleted);

    // Cleanup function to remove event listeners
    return () => {
      socket.off('comment:added', handleCommentAdded);
      socket.off('comment:updated', handleCommentUpdated);
      socket.off('comment:deleted', handleCommentDeleted);
    };
  }, [socket, leadId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    if (!leadId) {
      toast({
        title: 'Save the lead first',
        description: 'Please save the lead before adding comments',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/api/comments', {
        leadId,
        content: newComment,
        type: commentType,
      });
      
      // Emit socket event for real-time updates
      if (socket?.connected) {
        console.log('[SOCKET] Emitting comment:added event', { leadId });
        socket.emit('comment:added', { leadId });
      }
      
      setNewComment('');
      fetchComments();
      
      toast({
        title: 'Comment added',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('[NewKBCommentsForm] Error adding comment:', error);
      toast({
        title: 'Error adding comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/comments/${commentId}`);
      
      // Emit socket event for real-time updates
      if (socket?.connected) {
        console.log('[SOCKET] Emitting comment:deleted event', { leadId, commentId });
        socket.emit('comment:deleted', { leadId, commentId });
      }
      
      fetchComments();
      
      toast({
        title: 'Comment deleted',
        status: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('[NewKBCommentsForm] Error deleting comment:', error);
      toast({
        title: 'Error deleting comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Show a different UI if the lead hasn't been saved yet
  if (!isEditMode && !leadId) {
    return (
      <Box p={6} textAlign="center">
        <Text fontSize="md" color="gray.600" mb={4}>
          Save the lead first to add comments
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch" p={4}>
      <Box>
        <HStack mb={2}>
          <Select
            value={commentType}
            onChange={(e) => setCommentType(e.target.value)}
            w="150px"
          >
            <option value="general">General</option>
            <option value="internal">Internal</option>
            <option value="client">Client</option>
            <option value="vendor">Vendor</option>
          </Select>
          <Button
            colorScheme="blue"
            onClick={handleAddComment}
            isLoading={isLoading}
          >
            Add Comment
          </Button>
        </HStack>
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a new comment..."
          rows={3}
        />
      </Box>

      <Divider />

      {isLoading && comments.length === 0 ? (
        <Center p={8}>
          <Spinner size="md" color="blue.500" />
        </Center>
      ) : (
        <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
          {comments.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={8}>
              No comments yet
            </Text>
          ) : (
            comments.map((comment) => (
              <Box
                key={comment._id}
                p={3}
                borderWidth={1}
                borderRadius="md"
                position="relative"
                bg={comment.type === 'internal' ? 'blue.50' : 
                   comment.type === 'client' ? 'green.50' : 
                   comment.type === 'vendor' ? 'purple.50' : 'gray.50'}
              >
                <HStack justify="space-between" mb={2}>
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" color="gray.500">
                      {comment.timestamp ? new Date(comment.timestamp).toLocaleString() : new Date().toLocaleString()}
                    </Text>
                    <Text fontSize="sm" color="blue.600">
                      {(typeof comment.createdBy?.displayName === 'string' ? comment.createdBy?.displayName : '') || 
                       (typeof comment.createdBy?.email === 'string' ? comment.createdBy?.email : '') || 
                       'Unknown User'}
                    </Text>
                  </VStack>
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDeleteComment(comment._id)}
                    aria-label="Delete comment"
                  />
                </HStack>
                <Text>{typeof comment.content === 'string' ? comment.content : JSON.stringify(comment.content)}</Text>
                <HStack spacing={2} mt={2}>
                  <Text fontSize="sm" fontWeight="bold" color={
                    comment.type === 'internal' ? 'blue.600' : 
                    comment.type === 'client' ? 'green.600' : 
                    comment.type === 'vendor' ? 'purple.600' : 'gray.600'
                  }>
                    {typeof comment.type === 'string' ? comment.type.charAt(0).toUpperCase() + comment.type.slice(1) : 'General'}
                  </Text>
                </HStack>
              </Box>
            ))
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default NewKBCommentsForm;
